import { routes } from "../apiroutes";
import HttpService from "../httpServices";
import { ErrorHandler } from "../errorHandler";
import useFetchItem from "../useFetchItem";
import useMutateItem from "../useMutateItem";
import { showErrorAlert, showSuccessAlert } from "../../utils/alert";

const httpService = new HttpService();

// Subscription Management
export const useGetUserSubscription = ({ enabled = true } = {}) => {
  const { isFetched, isLoading, error, data, refetch, isFetching } = useFetchItem({
    queryKey: ["userSubscription"],
    queryFn: () => httpService.getData(routes.getUserSubscription()),
    enabled,
    retry: 2
  });



  return {
    subscriptionIsFetching: isFetching,
    subscriptionIsFetched: isFetched,
    subscriptionIsLoading: isLoading,
    subscriptionError: ErrorHandler(error),
    subscriptionData: data?.data || {},
    refetchSubscription: refetch
  };
};

export const useCancelSubscription = (handleSuccess) => {
  const { data, error, isPending, mutate } = useMutateItem({
    mutationFn: (payload) => httpService.postData(payload, routes.cancelSubscription()),
    onSuccess: (res) => {
      const resData = res?.data || {};
      showSuccessAlert(resData?.message || "Subscription cancelled successfully");
      handleSuccess?.(resData);
    },
    onError: (err) => {
      const error = ErrorHandler(err);
      showErrorAlert(error);
    },
  });

  return {
    cancelSubscriptionData: data,
    cancelSubscriptionError: ErrorHandler(error),
    cancelSubscriptionIsLoading: isPending,
    cancelSubscriptionPayload: (requestPayload) => mutate(requestPayload),
  };
};

export const useReactivateSubscription = (handleSuccess) => {
  const { data, error, isPending, mutate } = useMutateItem({
    mutationFn: () => httpService.postData({}, routes.reactivateSubscription()),
    onSuccess: (res) => {
      const resData = res?.data || {};
      showSuccessAlert(resData?.message || "Subscription reactivated successfully");
      handleSuccess?.(resData);
    },
    onError: (err) => {
      const error = ErrorHandler(err);
      showErrorAlert(error);
    },
  });

  return {
    reactivateSubscriptionData: data,
    reactivateSubscriptionError: ErrorHandler(error),
    reactivateSubscriptionIsLoading: isPending,
    reactivateSubscriptionPayload: () => mutate(),
  };
};

export const useUpdateSubscriptionPlan = (handleSuccess) => {
  const { data, error, isPending, mutate } = useMutateItem({
    mutationFn: (payload) => httpService.postData(payload, routes.updateSubscriptionPlan()),
    onSuccess: (res) => {
      const resData = res?.data || {};
      showSuccessAlert(resData?.message || "Plan updated successfully");
      handleSuccess?.(resData);
    },
    onError: (err) => {
      const error = ErrorHandler(err);
      showErrorAlert(error);
    },
  });

  return {
    updatePlanData: data,
    updatePlanError: ErrorHandler(error),
    updatePlanIsLoading: isPending,
    updatePlanPayload: (requestPayload) => mutate(requestPayload),
  };
};

// Payment Methods
export const useGetPaymentMethods = ({ enabled = true } = {}) => {
  const { isFetched, isLoading, error, data, refetch, isFetching } = useFetchItem({
    queryKey: ["paymentMethods"],
    queryFn: () => httpService.getData(routes.getPaymentMethods()),
    enabled,
    retry: 2
  });

  return {
    paymentMethodsIsFetching: isFetching,
    paymentMethodsIsFetched: isFetched,
    paymentMethodsIsLoading: isLoading,
    paymentMethodsError: ErrorHandler(error),
    paymentMethodsData: data?.data || [],
    refetchPaymentMethods: refetch
  };
};

export const useAddPaymentMethod = (handleSuccess) => {
  const { data, error, isPending, mutate } = useMutateItem({
    mutationFn: (payload) => httpService.postData(payload, routes.addPaymentMethod()),
    onSuccess: (res) => {
      const resData = res?.data || {};
      showSuccessAlert(resData?.message || "Payment method added successfully");
      handleSuccess?.(resData);
    },
    onError: (err) => {
      const error = ErrorHandler(err);
      showErrorAlert(error);
    },
  });

  return {
    addPaymentMethodData: data,
    addPaymentMethodError: ErrorHandler(error),
    addPaymentMethodIsLoading: isPending,
    addPaymentMethodPayload: (requestPayload) => mutate(requestPayload),
  };
};

export const useSetDefaultPaymentMethod = (handleSuccess) => {
  const { data, error, isPending, mutate } = useMutateItem({
    mutationFn: (id) => httpService.putData({}, routes.setDefaultPaymentMethod(id)),
    onSuccess: (res) => {
      const resData = res?.data || {};
      showSuccessAlert(resData?.message || "Payment method set as default");
      handleSuccess?.(resData);
    },
    onError: (err) => {
      const error = ErrorHandler(err);
      showErrorAlert(error);
    },
  });

  return {
    setDefaultPaymentMethodData: data,
    setDefaultPaymentMethodError: ErrorHandler(error),
    setDefaultPaymentMethodIsLoading: isPending,
    setDefaultPaymentMethodPayload: (id) => mutate(id),
  };
};

export const useDeletePaymentMethod = (handleSuccess) => {
  const { data, error, isPending, mutate } = useMutateItem({
    mutationFn: (id) => httpService.deleteData(routes.deletePaymentMethod(id)),
    onSuccess: (res) => {
      const resData = res?.data || {};
      showSuccessAlert(resData?.message || "Payment method removed successfully");
      handleSuccess?.(resData);
    },
    onError: (err) => {
      const error = ErrorHandler(err);
      showErrorAlert(error);
    },
  });

  return {
    deletePaymentMethodData: data,
    deletePaymentMethodError: ErrorHandler(error),
    deletePaymentMethodIsLoading: isPending,
    deletePaymentMethodPayload: (id) => mutate(id),
  };
};

// Billing & Payments
export const useGetUserBilling = ({ enabled = true } = {}) => {
  const { isFetched, isLoading, error, data, refetch, isFetching } = useFetchItem({
    queryKey: ["userBilling"],
    queryFn: () => httpService.getData(routes.getUserBilling()),
    enabled,
    retry: 2
  });



  return {
    billingIsFetching: isFetching,
    billingIsFetched: isFetched,
    billingIsLoading: isLoading,
    billingError: ErrorHandler(error),
    billingData: data?.data || {},
    refetchBilling: refetch
  };
};

export const useGetUserPayments = ({ enabled = true } = {}) => {
  const { isFetched, isLoading, error, data, refetch, isFetching } = useFetchItem({
    queryKey: ["userPayments"],
    queryFn: () => httpService.getData(routes.getUserPayments()),
    enabled,
    retry: 2
  });

  // Mock data for testing - remove when API is ready
  const mockPayments = [
    {
      id: "pi_1",
      created: "2025-08-15T10:30:00Z",
      description: "Growth Plan (Monthly) - August 2025",
      amount: 4900,
      currency: "usd",
      status: "succeeded",
      type: "subscription",
      receipt_url: "https://receipt.stripe.com/example1",
      invoice_pdf: "https://invoice.stripe.com/example1"
    },
    {
      id: "pi_2",
      created: "2025-07-15T10:30:00Z",
      description: "Growth Plan (Monthly) - July 2025",
      amount: 4900,
      currency: "usd",
      status: "succeeded",
      type: "subscription",
      receipt_url: "https://receipt.stripe.com/example2",
      invoice_pdf: "https://invoice.stripe.com/example2"
    },
    {
      id: "pi_3",
      created: "2025-06-15T10:30:00Z",
      description: "Growth Plan (Monthly) - June 2025",
      amount: 4900,
      currency: "usd",
      status: "succeeded",
      type: "subscription",
      receipt_url: "https://receipt.stripe.com/example3",
      invoice_pdf: "https://invoice.stripe.com/example3"
    },
    {
      id: "pi_4",
      created: "2025-05-15T10:30:00Z",
      description: "Growth Plan (Monthly) - May 2025",
      amount: 4900,
      currency: "usd",
      status: "succeeded",
      type: "subscription",
      receipt_url: "https://receipt.stripe.com/example4",
      invoice_pdf: "https://invoice.stripe.com/example4"
    },
    {
      id: "pi_5",
      created: "2025-04-15T10:30:00Z",
      description: "Growth Plan (Monthly) - April 2025",
      amount: 4900,
      currency: "usd",
      status: "succeeded",
      type: "subscription",
      receipt_url: "https://receipt.stripe.com/example5",
      invoice_pdf: "https://invoice.stripe.com/example5"
    },
    {
      id: "pi_6",
      created: "2025-03-15T10:30:00Z",
      description: "Growth Plan (Monthly) - March 2025",
      amount: 4900,
      currency: "usd",
      status: "succeeded",
      type: "subscription",
      receipt_url: "https://receipt.stripe.com/example6",
      invoice_pdf: "https://invoice.stripe.com/example6"
    },
    {
      id: "pi_7",
      created: "2025-02-15T10:30:00Z",
      description: "Growth Plan (Monthly) - February 2025",
      amount: 4900,
      currency: "usd",
      status: "succeeded",
      type: "subscription",
      receipt_url: "https://receipt.stripe.com/example7",
      invoice_pdf: "https://invoice.stripe.com/example7"
    },
    {
      id: "pi_8",
      created: "2025-01-15T10:30:00Z",
      description: "Growth Plan (Monthly) - January 2025",
      amount: 4900,
      currency: "usd",
      status: "succeeded",
      type: "subscription",
      receipt_url: "https://receipt.stripe.com/example8",
      invoice_pdf: "https://invoice.stripe.com/example8"
    },
    {
      id: "pi_9",
      created: "2024-12-15T10:30:00Z",
      description: "Growth Plan (Monthly) - December 2024",
      amount: 4900,
      currency: "usd",
      status: "succeeded",
      type: "subscription",
      receipt_url: "https://receipt.stripe.com/example9",
      invoice_pdf: "https://invoice.stripe.com/example9"
    },
    {
      id: "pi_10",
      created: "2024-11-15T10:30:00Z",
      description: "Growth Plan (Monthly) - November 2024",
      amount: 4900,
      currency: "usd",
      status: "succeeded",
      type: "subscription",
      receipt_url: "https://receipt.stripe.com/example10",
      invoice_pdf: "https://invoice.stripe.com/example10"
    },
    {
      id: "pi_11",
      created: "2024-10-15T10:30:00Z",
      description: "Growth Plan (Monthly) - October 2024",
      amount: 4900,
      currency: "usd",
      status: "succeeded",
      type: "subscription",
      receipt_url: "https://receipt.stripe.com/example11",
      invoice_pdf: "https://invoice.stripe.com/example11"
    },
    {
      id: "pi_12",
      created: "2024-09-15T10:30:00Z",
      description: "Growth Plan (Monthly) - September 2024",
      amount: 4900,
      currency: "usd",
      status: "succeeded",
      type: "subscription",
      receipt_url: "https://receipt.stripe.com/example12",
      invoice_pdf: "https://invoice.stripe.com/example12"
    }
  ];

  return {
    paymentsIsFetching: isFetching,
    paymentsIsFetched: isFetched,
    paymentsIsLoading: isLoading,
    paymentsError: ErrorHandler(error),
    paymentsData: data?.data || mockPayments, // Use mock data if API returns empty
    refetchPayments: refetch
  };
};

export const useGetBillingPortal = (handleSuccess) => {
  const { data, error, isPending, mutate } = useMutateItem({
    mutationFn: () => httpService.postData({}, routes.getBillingPortal()),
    onSuccess: (res) => {
      const resData = res?.data || {};
      handleSuccess?.(resData);
    },
    onError: (err) => {
      const error = ErrorHandler(err);
      showErrorAlert(error);
    },
  });

  return {
    billingPortalData: data,
    billingPortalError: ErrorHandler(error),
    billingPortalIsLoading: isPending,
    billingPortalPayload: () => mutate(),
  };
};

// Plans & Usage
export const useGetPlans = ({ enabled = true } = {}) => {
  const { isFetched, isLoading, error, data, refetch, isFetching } = useFetchItem({
    queryKey: ["plans"],
    queryFn: () => httpService.getData(routes.getPlans()),
    enabled,
    retry: 2
  });

  return {
    plansIsFetching: isFetching,
    plansIsFetched: isFetched,
    plansIsLoading: isLoading,
    plansError: ErrorHandler(error),
    plansData: data?.data || {},
    refetchPlans: refetch
  };
};

export const useGetPlansCompare = ({ enabled = true } = {}) => {
  const { isFetched, isLoading, error, data, refetch, isFetching } = useFetchItem({
    queryKey: ["plansCompare"],
    queryFn: () => httpService.getData(routes.getPlansCompare()),
    enabled,
    retry: 2
  });

  return {
    plansCompareIsFetching: isFetching,
    plansCompareIsFetched: isFetched,
    plansCompareIsLoading: isLoading,
    plansCompareError: ErrorHandler(error),
    plansCompareData: data?.data || {},
    refetchPlansCompare: refetch
  };
};

export const useGetUserUsage = ({ enabled = true } = {}) => {
  const { isFetched, isLoading, error, data, refetch, isFetching } = useFetchItem({
    queryKey: ["userUsage"],
    queryFn: () => httpService.getData(routes.getUserUsage()),
    enabled,
    retry: 2
  });

  return {
    usageIsFetching: isFetching,
    usageIsFetched: isFetched,
    usageIsLoading: isLoading,
    usageError: ErrorHandler(error),
    usageData: data?.data || {},
    refetchUsage: refetch
  };
}; 